/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { motion, cubicBezier } from 'framer-motion';
import { SparklesIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { AppPage, Generation } from '../types';
import TheEdit from '../components/TheEdit';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

interface TrendsPageProps {
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const TrendsPage: React.FC<TrendsPageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const [activeGen, setActiveGen] = React.useState<Generation | 'all'>('all');

    useSEO(
        t('trends_seo_title'),
        t('trends_seo_desc'),
        t('trends_seo_keywords').split(',').map(kw => kw.trim())
    );

    const sectionVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1) } },
    };

    const generations: (Generation | 'all')[] = ['all', 'alpha', 'z', 'millennial', 'classic'];

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
            className="h-full flex flex-col items-center bg-gray-50 dark:bg-[#18181B] text-gray-800 dark:text-[#F1F1F1] overflow-y-auto custom-scrollbar"
        >
            <div className="max-w-7xl w-full mx-auto px-6 py-16">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <SparklesIcon className="w-8 h-8 text-[#C0A172]" />
                        <span className="text-xs font-bold uppercase tracking-[0.3em] text-gray-400 dark:text-[#C0A172]">
                            {t('trends_page_subtitle')}
                        </span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] mb-6">
                        {t('trends_page_title')}
                    </h1>
                    <p className="text-lg text-gray-600 dark:text-[#F1F1F1]/70 max-w-2xl mx-auto mb-10">
                        {t('trends_page_description')}
                    </p>

                    {/* Generational Filters */}
                    <div className="flex flex-wrap justify-center gap-4 mb-8">
                        {generations.map((gen) => (
                            <button
                                key={gen}
                                onClick={() => setActiveGen(gen)}
                                className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest transition-all duration-300 ${activeGen === gen
                                        ? 'bg-[#C0A172] text-white shadow-lg'
                                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-[#C0A172]'
                                    }`}
                            >
                                {gen === 'all' ? t('trends_filter_all') : t(`generation_${gen}`)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* The Weekly Edit */}
                <TheEdit activeGeneration={activeGen} />
            </div>

            <Footer onNavigate={onNavigate} />
        </motion.div>
    );
};

export default TrendsPage;
